import { Box, Button, Skeleton, Typography } from '@mui/material';
import { memo, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavigationNodeRow } from '@/components/menu/NavigationNode';
import { navigationStudio } from '@/constants/navigationStudio';
import type {
  NavigationDropPosition,
  NavigationNode,
  NavigationNodeActionId,
} from '@/types/navigationConfig';

interface NavigationTreeProps {
  rows: Array<{ node: NavigationNode; depth: number }>;
  selectedNodeId: string | null;
  expandedIds: Set<string>;
  isLoading?: boolean;
  isEmpty?: boolean;
  onSelect: (nodeId: string) => void;
  onToggleExpand: (nodeId: string) => void;
  onMove: (
    dragId: string,
    targetId: string,
    position: NavigationDropPosition,
  ) => void;
  onAction: (actionId: NavigationNodeActionId, node: NavigationNode) => void;
  onCreateRoot?: () => void;
}

export const NavigationTree = memo(function NavigationTree({
  rows,
  selectedNodeId,
  expandedIds,
  isLoading = false,
  isEmpty = false,
  onSelect,
  onToggleExpand,
  onMove,
  onAction,
  onCreateRoot,
}: NavigationTreeProps) {
  const { t } = useTranslation('entity-portal');
  const [dropTargetId, setDropTargetId] = useState<string | null>(null);
  const [dropPosition, setDropPosition] =
    useState<NavigationDropPosition | null>(null);

  const handleDropTargetChange = useCallback(
    (targetId: string | null, position: NavigationDropPosition | null) => {
      setDropTargetId(targetId);
      setDropPosition(position);
    },
    [],
  );

  if (isLoading) {
    return (
      <Box
        sx={{
          border: 1,
          borderColor: navigationStudio.panelBorderColor,
          borderRadius: navigationStudio.panelBorderRadius,
          p: 2,
          bgcolor: navigationStudio.panelBackground,
        }}
      >
        {Array.from({ length: 6 }).map((_, index) => (
          <Skeleton key={index} height={44} sx={{ mb: 1, borderRadius: 1 }} />
        ))}
      </Box>
    );
  }

  if (isEmpty) {
    return (
      <Box
        sx={{
          border: '1px dashed',
          borderColor: navigationStudio.panelBorderColor,
          borderRadius: navigationStudio.panelBorderRadius,
          p: 5,
          textAlign: 'center',
          bgcolor: navigationStudio.panelBackground,
        }}
      >
        <Typography variant="subtitle1" gutterBottom>
          {t('navigationConfig.emptyTreeTitle')}
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: 3, maxWidth: 420, mx: 'auto' }}
        >
          {t('navigationConfig.emptyTreeDescription')}
        </Typography>
        {onCreateRoot ? (
          <Button variant="contained" onClick={onCreateRoot}>
            {t('navigationConfig.createRootMenu')}
          </Button>
        ) : null}
      </Box>
    );
  }

  return (
    <Box
      role="tree"
      aria-label={t('navigationConfig.treeLabel')}
      sx={{
        border: 1,
        borderColor: navigationStudio.panelBorderColor,
        borderRadius: navigationStudio.panelBorderRadius,
        overflow: 'hidden',
        bgcolor: navigationStudio.panelBackground,
      }}
    >
      <Box
        sx={{
          display: { xs: 'none', md: 'flex' },
          alignItems: 'center',
          gap: 1,
          px: navigationStudio.treeRowPaddingX,
          py: 1.25,
          pl: `${12}px`,
          bgcolor: navigationStudio.propertyPanelBg,
          borderBottom: 1,
          borderColor: navigationStudio.panelBorderColor,
        }}
      >
        <Box sx={{ width: 18, flexShrink: 0 }} />
        <Box sx={{ width: 28, flexShrink: 0 }} />
        <Box sx={{ width: 18, flexShrink: 0 }} />
        <Typography
          variant="tableHeader"
          sx={{ color: 'text.secondary', minWidth: 140, flex: '1 1 160px' }}
        >
          {t('navigationConfig.columnLabel')}
        </Typography>
        <Typography
          variant="tableHeader"
          sx={{ color: 'text.secondary', flex: '1 1 120px' }}
        >
          {t('navigationConfig.columnRoute')}
        </Typography>
        <Typography
          variant="tableHeader"
          sx={{ color: 'text.secondary', flexShrink: 0 }}
        >
          {t('navigationConfig.columnVisibility')}
        </Typography>
        <Box sx={{ width: 36, flexShrink: 0 }} />
      </Box>

      {rows.map(({ node, depth }) => (
        <NavigationNodeRow
          key={node.id}
          node={node}
          depth={depth}
          isSelected={node.id === selectedNodeId}
          isExpanded={expandedIds.has(node.id)}
          dropTargetId={dropTargetId}
          dropPosition={dropPosition}
          onSelect={onSelect}
          onToggleExpand={onToggleExpand}
          onMove={onMove}
          onDropTargetChange={handleDropTargetChange}
          onAction={onAction}
        />
      ))}
    </Box>
  );
});
